import React, { useEffect, useId, useRef, useState } from "react";
import {
  getNotificationPreference,
  setNotificationPreference,
} from "../../api";
import { handleApiError } from "../../utils/apiErrorHandler";
import publisherStore from "../../flux/publisherStore";
import { getPublishers } from "../../flux/publisherAction";
import {
  NotifAnalyst,
  NotifHead,
  NotifLoading,
  NotifMobileLabel,
  NotifRow,
  NotifSectionDesc,
  NotifSectionHead,
  NotifSections,
  NotifSectionTitle,
  NotifSpecialDesc,
  NotifSpecialList,
  NotifSpecialRow,
  NotifSpecialText,
  NotifSpecialTitle,
  NotifSwitch,
  NotifTable,
  NotifToggle,
  NotifToggleGroup,
  NotifToggles,
  ProfileCard,
  ProfileCardDesc,
  ProfileCardHead,
  ProfileCardIcon,
  ProfileCardTitle,
} from "./Profile.styles";

const platforms = {
  email: 1,
  platform: 3,
};

const BellIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

const LoaderIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const ChannelToggle = ({ id, label, checked, onChange, disabled }) => (
  <NotifToggle htmlFor={id}>
    <NotifSwitch
      id={id}
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      $on={!!checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
    {label}
  </NotifToggle>
);

const PrefToggles = ({
  idPrefix,
  mobileLabel,
  emailChecked,
  platformChecked,
  onEmailChange,
  onPlatformChange,
  disabled,
}) => (
  <NotifToggleGroup>
    <NotifMobileLabel>{mobileLabel}</NotifMobileLabel>
    <NotifToggles>
      <ChannelToggle
        id={`${idPrefix}-email`}
        label="Email"
        checked={emailChecked}
        onChange={onEmailChange}
        disabled={disabled}
      />
      <ChannelToggle
        id={`${idPrefix}-platform`}
        label="Platform"
        checked={platformChecked}
        onChange={onPlatformChange}
        disabled={disabled}
      />
    </NotifToggles>
  </NotifToggleGroup>
);

const channelIds = (list = []) => list.map((p) => p.notificationChannelId);

const hasChannel = (ids, channelId) => ids.indexOf(channelId) !== -1;

/**
 * Lovable notifications layout + Switch toggles.
 * Prod behavior: instant setNotificationPreference on each toggle (no Save/Cancel).
 */
const NotificationsPanel = () => {
  const uid = useId();
  const [preferences, setPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [publishers, setPublishers] = useState(
    publisherStore.getPublishers() || []
  );

  useEffect(() => {
    getPublishers();
    const onChange = () => setPublishers(publisherStore.getPublishers() || []);
    publisherStore.on("change", onChange);
    return () => publisherStore.removeListener("change", onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = localStorage.getItem("user");
        if (!raw) return;
        const user = JSON.parse(raw);
        const preferencesRes = await getNotificationPreference(user.id);
        if (!cancelled) setPreferences(preferencesRes.data);
      } catch {
        /* keep null */
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const persistPreferences = async (previous, next) => {
    const raw = localStorage.getItem("user");
    if (!raw || savingRef.current) return;
    const user = JSON.parse(raw);
    if (!user?.id) return;
    savingRef.current = true;
    setPreferences(next);
    setSaving(true);
    try {
      const res = await setNotificationPreference(user.id, next);
      if (res && res.status >= 200 && res.status < 300) return;
      setPreferences(previous);
      handleApiError(res, "Couldn't update notification settings");
    } catch (error) {
      setPreferences(previous);
      handleApiError(error, "Couldn't update notification settings");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const togglePref = (analystId, platform, hasItem, type) => {
    if (!preferences || savingRef.current) return;
    const temp = { ...preferences };
    temp[type] = [...(temp[type] || [])];
    if (hasItem) {
      temp[type] = temp[type].filter(
        (p) =>
          !(
            p.notificationChannelId === platforms[platform] &&
            p.publisherId === analystId
          )
      );
    } else {
      temp[type].push({
        publisherId: analystId,
        notificationChannelId: platforms[platform],
      });
    }
    persistPreferences(preferences, temp);
  };

  const changeSpecial = (key, platform, hasItem) => {
    if (!preferences || savingRef.current) return;
    const temp = {
      ...preferences,
      special: { ...preferences.special },
    };
    if (!hasItem) {
      temp.special[key] = [
        ...(temp.special[key] || []),
        { notificationChannelId: platforms[platform] },
      ];
    } else {
      temp.special[key] = (temp.special[key] || []).filter(
        (p) => p.notificationChannelId !== platforms[platform]
      );
    }
    persistPreferences(preferences, temp);
  };

  const visiblePublishers = (publishers || []).filter(
    (p) => p.isHidden === false
  );

  const special = preferences?.special || {};
  const trackingChannels = channelIds(special.alertsImTracking);
  const replyChannels = channelIds(special.commentReply);
  const alertsImTrackingHasEmail = hasChannel(trackingChannels, 1);
  const alertsImTrackingHasPlatform = hasChannel(trackingChannels, 3);
  const publisherPostRepliesHasEmail = hasChannel(replyChannels, 1);
  const publisherPostRepliesHasPlatform = hasChannel(replyChannels, 3);
  const togglesReady = !!preferences && !saving;

  return (
    <ProfileCard>
      <ProfileCardHead>
        <ProfileCardIcon>
          <BellIcon />
        </ProfileCardIcon>
        <div>
          <ProfileCardTitle>Notifications</ProfileCardTitle>
          <ProfileCardDesc>
            Choose when and how you hear from analysts, alerts, and community
            replies.
          </ProfileCardDesc>
        </div>
      </ProfileCardHead>

      <NotifSections>
        <section>
          <NotifSectionHead>
            <NotifSectionTitle>Analyst notifications</NotifSectionTitle>
            <NotifSectionDesc>
              Control email and platform alerts for each analyst you follow.
            </NotifSectionDesc>
          </NotifSectionHead>

          <NotifTable>
            <NotifHead>
              <span>Analyst</span>
              <span style={{ textAlign: "center" }}>Posts</span>
              <span style={{ textAlign: "center" }}>Alerts</span>
            </NotifHead>

            {isLoading ? (
              <NotifLoading>
                <LoaderIcon />
                Loading preferences…
              </NotifLoading>
            ) : (
              visiblePublishers.map((user) => {
                const alertChannels = channelIds(
                  (preferences?.alerts || []).filter(
                    (p) => p.publisherId === user.id
                  )
                );
                const postChannels = channelIds(
                  (preferences?.posts || []).filter(
                    (p) => p.publisherId === user.id
                  )
                );
                const alertHasEmail = hasChannel(alertChannels, 1);
                const alertHasPlatform = hasChannel(alertChannels, 3);
                const postHasEmail = hasChannel(postChannels, 1);
                const postHasPlatform = hasChannel(postChannels, 3);
                const avatar =
                  user.avatar?.small ||
                  require("../../images/avatars/0.jpg");

                return (
                  <NotifRow key={user.id}>
                    <NotifAnalyst>
                      <img src={avatar} alt="" />
                      <span>{user.name}</span>
                    </NotifAnalyst>
                    <PrefToggles
                      idPrefix={`${uid}-posts-${user.id}`}
                      mobileLabel="Posts"
                      emailChecked={postHasEmail}
                      platformChecked={postHasPlatform}
                      disabled={!togglesReady}
                      onEmailChange={() =>
                        togglePref(user.id, "email", postHasEmail, "posts")
                      }
                      onPlatformChange={() =>
                        togglePref(
                          user.id,
                          "platform",
                          postHasPlatform,
                          "posts"
                        )
                      }
                    />
                    <PrefToggles
                      idPrefix={`${uid}-alerts-${user.id}`}
                      mobileLabel="Alerts"
                      emailChecked={alertHasEmail}
                      platformChecked={alertHasPlatform}
                      disabled={!togglesReady}
                      onEmailChange={() =>
                        togglePref(user.id, "email", alertHasEmail, "alerts")
                      }
                      onPlatformChange={() =>
                        togglePref(
                          user.id,
                          "platform",
                          alertHasPlatform,
                          "alerts"
                        )
                      }
                    />
                  </NotifRow>
                );
              })
            )}
          </NotifTable>
        </section>

        <section>
          <NotifSectionHead>
            <NotifSectionTitle>Special notifications</NotifSectionTitle>
            <NotifSectionDesc>
              Manage alerts for your tracked items and conversation activity.
            </NotifSectionDesc>
          </NotifSectionHead>

          {isLoading ? (
            <NotifSpecialList>
              <NotifLoading>
                <LoaderIcon />
                Loading preferences…
              </NotifLoading>
            </NotifSpecialList>
          ) : (
            <NotifSpecialList>
              <NotifSpecialRow>
                <NotifSpecialText>
                  <NotifSpecialTitle>Alerts I&apos;m tracking</NotifSpecialTitle>
                  <NotifSpecialDesc>
                    Notifications for price and market alerts you&apos;ve set.
                  </NotifSpecialDesc>
                </NotifSpecialText>
                <NotifToggles>
                  <ChannelToggle
                    id={`${uid}-tracking-email`}
                    label="Email"
                    checked={alertsImTrackingHasEmail}
                    disabled={!togglesReady}
                    onChange={() =>
                      changeSpecial(
                        "alertsImTracking",
                        "email",
                        alertsImTrackingHasEmail
                      )
                    }
                  />
                  <ChannelToggle
                    id={`${uid}-tracking-platform`}
                    label="Platform"
                    checked={alertsImTrackingHasPlatform}
                    disabled={!togglesReady}
                    onChange={() =>
                      changeSpecial(
                        "alertsImTracking",
                        "platform",
                        alertsImTrackingHasPlatform
                      )
                    }
                  />
                </NotifToggles>
              </NotifSpecialRow>

              <NotifSpecialRow>
                <NotifSpecialText>
                  <NotifSpecialTitle>
                    Publisher comment replies
                  </NotifSpecialTitle>
                  <NotifSpecialDesc>
                    Get notified when an analyst or publisher replies to your
                    comment.
                  </NotifSpecialDesc>
                </NotifSpecialText>
                <NotifToggles>
                  <ChannelToggle
                    id={`${uid}-replies-email`}
                    label="Email"
                    checked={publisherPostRepliesHasEmail}
                    disabled={!togglesReady}
                    onChange={() =>
                      changeSpecial(
                        "commentReply",
                        "email",
                        publisherPostRepliesHasEmail
                      )
                    }
                  />
                  <ChannelToggle
                    id={`${uid}-replies-platform`}
                    label="Platform"
                    checked={publisherPostRepliesHasPlatform}
                    disabled={!togglesReady}
                    onChange={() =>
                      changeSpecial(
                        "commentReply",
                        "platform",
                        publisherPostRepliesHasPlatform
                      )
                    }
                  />
                </NotifToggles>
              </NotifSpecialRow>
            </NotifSpecialList>
          )}
        </section>
      </NotifSections>
    </ProfileCard>
  );
};

export default NotificationsPanel;
